import React from 'react';
import { connect } from 'react-redux';
import { Row, Col, Button, Table, Alert, Clearfix, OverlayTrigger, Tooltip, Label } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import moment from 'moment';
import 'moment-timezone';
import { findIndex as _findIndex } from 'lodash';

import { addNotification } from 'actions/NotificationsActions';
import { deleterDispatchHelper } from 'actions/DeleterActions';
import { getNameInCurrentLanguage } from 'helpers/intl';
import { getViewerDocFiles } from 'helpers/fileViewer';
import { getDocSize, docIcons, docFirstFileName, docFileNames } from 'helpers/docs';

import Entity from 'components/Entity';
import List from 'components/List';
import Data from 'components/Data';
import RichEditorContent from 'components/RichEditorContent';

import { Viewer } from 'components/viewer';

import SimplePrompt from 'components/layout/SimplePrompt';
import Loader from 'components/layout/Loader';
import IntlDate from 'components/layout/IntlDate';

import QuotesList from 'components/layout/financial/QuotesList';
import InvoicesList from 'components/layout/financial/InvoicesList';

import IntlFormInlineSelector from 'components/intlForm/IntlFormInlineSelector';

import LeadDocumentNew from 'components/modals/LeadDocumentNew';
import DocumentReview from 'components/modals/DocumentReview';

import { entityMetaMapping, ROLE_CRM_MASTER } from 'js/constants';
import may from 'js/security';

class LeadDocs extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showNew: false,
      showEdit: false,
      editedDocument: null,
      shownIndex: null,
      viewerIndex: null,
      showFileViewer: false,
      entityLoaded: false,
      entityChanged: false,
      reviewDoc: null,
      showReview: false,
    };

    this.entity = new Entity();

    if (props.meta.DocsList !== undefined && props.meta[entityMetaMapping.doc] !== undefined) {
      this.entity.setMeta(props.meta, 'DocsList');
    }
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      data,
      docNewFormStatus,
      docEditFormStatus,
      docReviewFormStatus,
      doNotify,
    } = this.props;
    const { entity } = this.state;

    // document was saved to the lead
    if (prevProps.docNewFormStatus.pending && !docNewFormStatus.pending && !docNewFormStatus.hasError) {
      this.closeNew();
      doNotify(1, messages.document_saved);
    }

    // document was saved to the lead
    if (prevProps.docEditFormStatus.pending && !docEditFormStatus.pending && !docEditFormStatus.hasError) {
      this.closeEdit();
      doNotify(1, messages.document_saved);
    }

    if (prevProps.docReviewFormStatus.pending && !docReviewFormStatus.pending && !docReviewFormStatus.hasError) {
      this.closeReview();
      doNotify(1, messages.document_reviewed);

      const index = _findIndex(entity.getList(), ['id', docReviewFormStatus.response.id]);
      if (index > -1) {
        const newEntity = {
          ...entity.updateValue('nextReviewDue', docReviewFormStatus.response.next_review_due, [
            'list',
            index.toString(),
          ]),
        };
        this.changeEntity(newEntity);
      }
    }

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData({ list: data.data.list });
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    } else if (prevProps.data.data.list.length !== data.data.list.length) {
      this.entity.setData({ list: data.data.list });
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (
      meta[entityMetaMapping.doc] !== undefined &&
      !meta[entityMetaMapping.doc].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, 'DocsList');
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  handleClick = index => {
    this.setState(prevState => ({ shownIndex: index === prevState.shownIndex ? null : index }));
  };

  closeDetails = () => {
    this.setState({ shownIndex: null });
  };

  showNew = () => {
    this.setState({ showNew: true });
  };

  closeNew = () => {
    this.setState({ showNew: false });
  };

  addDocument = data => {
    const { entity } = this.state;
    const newDocument = entity.createValue('list', [], data);
    const newValue = entity.getList().slice();
    newValue.splice(0, 0, newDocument);
    const newEntity = { ...entity.updateValue('list', newValue, []) };

    this.changeEntity(newEntity);
  };

  // dispatch remove action
  handleRemoveClick = (e, i, docId) => {
    e.preventDefault();
    e.stopPropagation();

    const { deleteDoc } = this.props;
    deleteDoc(docId);
  };

  // remove action was successfull, remove from array
  removeEntry = id => {
    const { entity } = this.state;
    const newValue = entity.getList().slice();
    // loop to find the id
    const newEntity = { ...entity.updateValue('list', newValue, []) };

    this.changeEntity(newEntity);
  };

  changeEntity = (entity, callback = null) => {
    this.setState({ entity, entityChanged: true }, callback);
  };

  showDoc = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      viewerIndex: index,
      showFileViewer: true,
    });
  };

  showReview = (e, reviewDoc) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      showReview: true,
      reviewDoc,
    });
  };

  closeReview = () => {
    this.setState({ showReview: false });
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      leadEntity,
      leadEntityLoaded,
      data,
      quotes,
      quoteTemplates,
      invoices,
      user,
    } = this.props;
    const {
      shownIndex,
      entityLoaded,
      entity,
      viewerIndex,
      showFileViewer,
      showNew,
      reviewDoc,
      showReview,
    } = this.state;

    const columns = [messages.name, messages.type, messages.file, messages.actions];
    let tBody = [];
    if (entityLoaded && entity.getList().length) {
      const documents = entity.getList();

      tBody = documents.map((doc, i) => {
        let overdue = false;
        if (doc.getNextReviewDue()) {
          if (moment(doc.getNextReviewDue()).isBefore()) {
            overdue = true;
          }
        }

        return (
          <tr
            key={i}
            className={(i === shownIndex ? 'shown ' : '') + (i % 2 ? 'even ' : 'odd ')}
            onClick={() => this.handleClick(i)}
          >
            <td className="v-align-middle semi-bold">{doc.getName()}</td>
            <td className="v-align-middle">
              {!!doc.getType() && (
                <React.Fragment>
                  <IntlFormInlineSelector />
                  {getNameInCurrentLanguage(doc.getType().getName(), appIntl)}
                  {overdue && (
                    <div class="m-t-10">
                      <Alert bsStyle="warning" class="no-margin">
                        <Clearfix>
                          <p class="col-xs-12 col-md-7 no-padding no-margin">
                            {messages.document_overdue_please_review}
                          </p>
                          <p class="col-xs-12 col-md-5 no-padding no-margin text-right bold">
                            <a href="#review" class="text-warning" onClick={e => this.showReview(e, doc)}>
                              {messages.review}
                            </a>
                          </p>
                        </Clearfix>
                      </Alert>
                    </div>
                  )}
                </React.Fragment>
              )}
              {!!doc.getDocTemplate() && !!doc.getDocTemplate().getId() && (
                <React.Fragment>
                  {messages.document_template} {doc.getDocTemplate().getName()}
                </React.Fragment>
              )}
            </td>
            <td className="v-align-middle">
              {docIcons(data)} {docFirstFileName(data)}{' '}
              <span className="hint-text">
                <small>{getDocSize(doc)}</small>
              </span>
            </td>
            <td className="v-align-middle text-right">
              {i === shownIndex ? (
                <Button bsSize="sm" className="btn-rounded m-r-10">
                  <i className="fa fa-times" /> {messages.close}
                </Button>
              ) : (
                <Button bsSize="sm" className="btn-rounded m-r-10">
                  <i className="fa fa-info" /> {messages.details}
                </Button>
              )}
              <Button bsSize="sm" className="btn-rounded m-r-10" onClick={e => this.showDoc(e, i)}>
                <i className="fa fa-eye" /> {messages.view}
              </Button>
              {may(ROLE_CRM_MASTER, user.roles) && (
                <SimplePrompt onSuccess={e => this.handleRemoveClick(e, i, doc.getId())} />
              )}
            </td>
          </tr>
        );
      });
    }

    if (shownIndex !== null && entity.getList()[shownIndex]) {
      const doc = entity.getList()[shownIndex];

      // user wants to see a location form
      tBody.splice(
        shownIndex + 1,
        0,
        <tr key="details-showing" className="row-details">
          <td colSpan={columns.length}>
            <Row>
              <Col xs={12} md={4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p>{messages.description}</p>
                    {!!doc.getDescription() && (
                      <RichEditorContent
                        content={'<div>' + (doc.getDescription() ? doc.getDescription() : '') + '</div>'}
                      />
                    )}
                    <dl>
                      {doc.getType().getRequireExpirationDate() && (
                        <React.Fragment>
                          <dt>{messages.doc_expiration_date}</dt>
                          <dd>
                            <IntlDate date={doc.getExpirationDate()} />
                          </dd>
                        </React.Fragment>
                      )}
                      {doc.getType().getRequireIssueDate() && (
                        <React.Fragment>
                          <dt class="m-t-5">{messages.doc_issue_date}</dt>
                          <dd>
                            <IntlDate date={doc.getIssueDate()} />
                          </dd>
                        </React.Fragment>
                      )}
                      {doc.getType().getRequireIssuingAuthority() && (
                        <React.Fragment>
                          <dt class="m-t-5">{messages.doc_issuing_authority}</dt>
                          <dd>{doc.getIssuingAuthority()}</dd>
                        </React.Fragment>
                      )}
                      {doc.getType().getRequireIssuingCity() && (
                        <React.Fragment>
                          <dt class="m-t-5">{messages.doc_issuing_city}</dt>
                          <dd>{doc.getIssuingCity()}</dd>
                        </React.Fragment>
                      )}
                      {doc.getType().getRequireNumber() && (
                        <React.Fragment>
                          <dt class="m-t-5">{messages.doc_number}</dt>
                          <dd>{doc.getNumber()}</dd>
                        </React.Fragment>
                      )}
                    </dl>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p>{messages.infos}</p>
                    <dl>
                      <dt>{messages.file}</dt>
                      <dd>
                        {docIcons(doc)} {docFirstFileName(doc)}
                        {!!doc.getFiles() && !!doc.getFiles().length > 1 && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={doc.getId() + 'files-list'}>
                                {docFileNames(doc).map(docFileName => (
                                  <div key={docFileName}>{docFileName}</div>
                                ))}
                              </Tooltip>
                            }
                          >
                            <Label className="m-l-5">
                              ... {doc.getFiles().length} {messages.files}
                            </Label>
                          </OverlayTrigger>
                        )}
                      </dd>

                      {!!doc.getType() && (
                        <React.Fragment>
                          <dt class="m-t-5">{messages.type}</dt>
                          <dd>
                            <IntlFormInlineSelector /> {getNameInCurrentLanguage(doc.getType().getName(), appIntl)}
                          </dd>
                        </React.Fragment>
                      )}

                      <dt className="m-t-5">{messages.file_size}</dt>
                      <dd>{getDocSize(doc)}</dd>

                      <dt className="m-t-5">{messages.file_uploaded}</dt>
                      <dd>
                        <IntlDate date={doc.getCdate()} />
                      </dd>
                    </dl>

                    <div className="text-right">
                      <Button onClick={e => this.showDoc(e, shownIndex)}>
                        <i className="fa fa-eye" /> {messages.view}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </td>
        </tr>
      );
    }

    let filterInvoices = null;
    if (leadEntityLoaded) {
      filterInvoices = { filters: { lead: leadEntity.getId() } };
      const assocOrders = leadEntity.getOrders();
      if (null !== assocOrders && assocOrders.length > 0) {
        const idMasterOrder = assocOrders[0].getId();
        filterInvoices = { idAnyOrder: idMasterOrder };
      }
    }

    return leadEntityLoaded ? (
      <div className="clearfix padding-20 sm-padding-10">
        <List
          noRender
          listName="leadDocuments"
          url="/api/documents.json"
          urlData={{ filters: { lead: leadEntity.getId() } }}
          list={data}
        />
        <List
          noRender
          listName="quotes"
          url="/api/quotes.json"
          urlData={{ filters: { lead: leadEntity.getId() } }}
          list={quotes}
        />
        <List
          noRender
          listName="quoteTemplates"
          url="/api/docs/templates.json"
          urlData={{ filters: { type: 'quote', language: appIntl.locale } }}
          list={quoteTemplates}
        />
        <List
          noRender
          listName="invoices"
          url="/api/invoices.json"
          urlData={filterInvoices}
          list={invoices}
        />
        <Data dataName="doc" />
        <Data dataName="docsList" />

        {entityLoaded ? (
          <Row className="m-b-15">
            <Col xs={12}>
              <h3>{messages.files}</h3>
              <Table responsive hover className="table-detailed">
                <thead>
                  <tr>
                    {columns.map((column, i) => {
                      return (
                        <th key={i} className={column === messages.actions ? 'text-right' : ''}>
                          {column}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <ReactCSSTransitionGroup
                  transitionName="tablerows"
                  transitionEnterTimeout={500}
                  transitionLeaveTimeout={1}
                  component="tbody"
                >
                  {tBody}
                </ReactCSSTransitionGroup>
              </Table>

              <div className="m-t-10">
                <Button onClick={this.showNew} className="m-r-10">
                  {messages.add_document}
                </Button>
              </div>
            </Col>

            <Viewer
              activeIndex={viewerIndex}
              show={showFileViewer}
              close={() => {
                this.setState({ showFileViewer: false });
              }}
              files={getViewerDocFiles(entity.getList())}
            />
          </Row>
        ) : (
          <Loader />
        )}

        {quotes.pending ? (
          <Loader />
        ) : (
          <Row>
            <Col xs={12}>
              <h3>{messages.salesDoc.quotes}</h3>
              <QuotesList list={quotes.data.list} />
            </Col>
          </Row>
        )}

        {invoices.pending ? (
          <Loader />
        ) : (
          <Row>
            <Col xs={12}>
              <h3>{messages.salesDoc.invoices}</h3>
              <InvoicesList list={invoices.data.list} listName="invoices" />
            </Col>
          </Row>
        )}

        <div id="document-new">
          <LeadDocumentNew lead={leadEntity} show={showNew} close={this.closeNew} />
        </div>
        <div id="document-review">
          <DocumentReview doc={reviewDoc} show={showReview} close={this.closeReview} />
        </div>
      </div>
    ) : null;
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    data: state.list.leadDocuments,
    quotes: state.list.quotes,
    invoices: state.list.invoices,
    quoteTemplates: state.list.quoteTemplates,
    meta: state.entityMeta,
    docNewFormStatus: state.formStatus.leadDocumentNew,
    docEditFormStatus: state.formStatus.leadDocumentEdit,
    docReviewFormStatus: state.formStatus.documentReview,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    deleteDoc: docId => {
      deleterDispatchHelper('leadDoc', '/api/documents/' + docId + '.json', dispatch);
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LeadDocs)
);
